<template>
  <TableTemplate
    v-model="tableResult"

    :columns="tableColumns"
    :columnVariables="tableColumnVariables"
    :skeletonClass="skeletonClass"
    :selected="selected"
    :enable-sorting="true"
    :remove-checkboxes="true"

    :page="page"
    :max="max"

    :on-row-click="selectRow"
    @update-column-order="updateColumnOrder"
    @set-page="v => page = v"

    id="klantoverzicht"
    ref="table"
  >
    <template #above>
      <TableHeader
        :title="`Advies bestelling (${filteredResult.length}/${result.length})`"
        :show-button="showButton"
        :alwaysShowMultiSelectCheckbox="true"
        :refetch="refetch"
        :exportParams="exportEndpointParams"
        export-endpoint="/drop/export/csv"
        csv-name="advice-orders"
        export-endpoint-type="post"

        @customize-layout="customizeLayout"
      >
        <template #header-buttons-before="props">
          <slot name="header-buttons-before" v-bind="props"></slot>
        </template>
      </TableHeader>

      <div class="d-flex">
        <TableMainSearchField v-model="search"/>
        <RefreshBtn
          :loading="isLoadingTableResult"
          @click.native="refreshAdviceOrder"
        />
      </div>

      <div class="mt-3">
        <span class="tag" :style="tagColors['brand']">
          Product Merk
        </span>
        <span class="tag" :style="tagColors['category']">
          Product Categorie
        </span>
        <span class="tag" :style="tagColors['added']">
          Toegevoegd sinds
        </span>
        <span class="tag" :style="tagColors['supplier']">
          Leverancier
        </span>
      </div>
    </template>

    <template #right-column>
      <card class="graph-wrapper d-flex mb-4 mt-lg-0 ml-lg-2 w-100">
        <div v-if="!selectedProduct" class="d-flex justify-content-center empty-placeholder-wrapper">
          <EmptyStatePlaceholder key="empty-staet"
            title="Selecteer een item om de details te zien."
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
            </svg>
          </EmptyStatePlaceholder>
        </div>
        <div v-else class="p-3 graph-details">
          <h4>Verkoop overzicht</h4>
          <p>Verkocht per week - tot 1 jaar terug</p>

          <highcharts :options="chartOptions" style="height: 230px"></highcharts>

          <div class="section"></div>

          <h4>Engine analyse</h4>
          <p>
            Huidige voorraad is: <b>{{ selectedProduct.currentStock }}</b>
          </p>
          <p>
            De engine verwacht niet meer dan <b>{{selectedProduct.engine.baseSupplyNeeded}}</b> verkochte producten in een week.
          </p>
          <p>
            De engine adviseert een minimale voorraad van: <b>{{selectedProduct.engine.baseSupplyPlusAdditionalPercentage}}</b>
          </p>
          <p>
            Bestel dus: <b>{{selectedProduct.engine.toOrder}}</b>
          </p>

          <div class="section"></div>

          <div class="row">
            <div class="col-md-6">
              <h4 class="my-1">Verander besteladvies</h4>
              <form class="text-muted" @submit.prevent="updateProductOrder">
                <div class="form-group mb-1" style="width: 100%">
                  <p>Bestel: <input type="number" id="change-advice" class="form-control form-control-sm" v-model="productOrderOverride[selectedProduct.id].amount"/></p>
                </div>
                <div class="form-group mb-1" style="width: 100%">
                  <p>Reden: <input type="text" class="form-control form-control-sm"  v-model="productOrderOverride[selectedProduct.id].reason"/></p>
                </div>
                <input type="submit" class="mt-2 btn btn-primary" value="OPSLAAN">
                <label v-if="productOrderOverrideSaved === selectedProduct.id" style="color: green; display: block">Opgeslagen.</label>
              </form>
            </div>
            <div class="col-md-6" style="border-left: 1px solid #ddd;">
              <h4 class="my-1">Instellingen</h4>
              <form class="text-muted" @submit.prevent="updateProductSettings">
                <div class="form-group mb-1" style="width: 100%">
                  <p>Leverancier: <input type="text" class="form-control form-control-sm"  v-model="form.productSettings.supplier"/></p>
                </div>
                <div class="form-group mb-1" style="width: 100%">
                  <p class="d-flex align-items-center">
                    <input type="checkbox" class="mr-2 form-control form-control-sm " style="width: 20px;" v-model="form.productSettings.stopped">
                    Stop met bijbestellen:
                  </p>
                </div>
                <input type="submit" class="mt-2 btn btn-primary" value="OPSLAAN">
                <label v-if="productSettingsSaved === selectedProduct.id" style="color: green; display: block">Opgeslagen.</label>
              </form>
            </div>
          </div>
        </div>
      </card>
    </template>

    <template #cell-name="{value, item}">
      <div class="my-2">
        <span>
          {{ value }}
        </span>
        <div class="d-flex mt-1">
          <span v-if="item.brand" class="tag" :style="tagColors['brand']">
            {{ item.brand }}
          </span>
          <span v-if="item.category" class="tag" :style="tagColors['category']">
            {{ item.category }}
          </span>
          <span v-if="item.date_added" class="tag" :style="tagColors['added']">
            {{ item.date_added | moment('from') }}
          </span>
          <span v-if="item.supplier" class="tag" :style="tagColors['supplier']">
            {{ item.supplier }}
          </span>
        </div>
      </div>
    </template>

    <template #cell-engine="{value}">
      <span class="d-flex align-items-center">
        <span class="dot mr-2" :style="{backgroundColor: value && value.color}"></span>
        {{ value.value }}
      </span>
    </template>

    <template>
    </template>

    <template #cell-orders_7d="{item}">
      <span class="my-2" :class="{'text-success': item.pct_7d > 0, 'text-danger': item.pct_7d < 0}">
        {{ item.orders_7d }}
        <div>
          {{ item.pct_7d_parsed }}
        </div>
      </span>
    </template>

    <template #cell-orders_1m="{item}">
      <span class="my-2" :class="{'text-success': item.pct_1m > 0, 'text-danger': item.pct_1m < 0}">
        {{ item.orders_1m }}
        <div>
          {{ item.pct_1m_parsed }}
        </div>
      </span>
    </template>

    <template #cell-orders_1y="{item}">
      <span class="my-2" :class="{'text-success': item.pct_1y > 0, 'text-danger': item.pct_1y < 0}">
        {{ item.orders_1y }}
        <div>
          {{ item.pct_1y_parsed }}
        </div>
      </span>
    </template>

    <template #cell-pct_1y="{value}">
      <span>
        € {{ value }}
      </span>
    </template>

    <template #empty-state>
      <EmptyStatePlaceholder
        title="Er zijn geen adviesbestellingen"
      >
        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">

          <g transform="matrix(3.4285714285714284,0,0,3.4285714285714284,0,0)"><g>
            <path d="M4.5,12.5l-4,1,1-3v-9a1,1,0,0,1,1-1h10a1,1,0,0,1,1,1v10a1,1,0,0,1-1,1Z" style="fill: none;stroke: currentColor;stroke-linecap: round;stroke-linejoin: round"></path>
            <line x1="7.5" y1="3" x2="7.5" y2="6" style="fill: none;stroke: currentColor;stroke-linecap: round;stroke-linejoin: round"></line>
            <circle cx="7.5" cy="9" r="0.5" style="fill: none;stroke: currentColor;stroke-linecap: round;stroke-linejoin: round"></circle>
          </g></g>
        </svg>
      </EmptyStatePlaceholder>
    </template>

  </TableTemplate>
</template>

<script>
import TableTemplate from 'src/layout/Table/TableTemplate'
import tableFieldOptions from "src/mixins/tableFieldOptions"
import EmptyStatePlaceholder from "src/layout/EmptyStatePlaceholder.vue"
import TableHeader from "src/components/Product/ProductTable/TableHeader.vue"
import TableMainSearchField from '../layout/TableMainSearchField.vue'
import TableDatePickerField from '../components/Inputs/TableDatePickerField.vue'
import RefreshBtn from '../layout/RefreshBtn.vue'

import table from "src/mixins/table"
import selectMultipleItems from "src/mixins/selectMultipleItems"
import TableCell from '../components/Table/TableCell.vue'
import { filters } from "src/utils/advice-order-filters"

import mom from "moment"

const BRANDS_TITLE = "Product Merk Filter"
const SUPPLIERS_TITLE = "Leverancier Filter"

export default {
  metaInfo: {
    title: "Advies bestelling",
  },
  components: {
    TableHeader,
    TableMainSearchField,
    RefreshBtn,
    EmptyStatePlaceholder,
    TableTemplate,
    TableDatePickerField,
    TableCell
},
  mixins: [
    selectMultipleItems,
    tableFieldOptions,
    table({
      pageReference: 'advice-orders',
      
      manualPagination: true,
      disableAlphabeticalSort: true,
      disablePageChangeRefetch: true,

      columnTranslationMaps: {
        "ID": "id",
        "Naam": "name",
        "Voorraad": "currentStock",
        "7d": "orders_7d",
        "1M": "orders_1m",
        "1Y": "orders_1y",
        "Omzet 1Y": "pct_1y",
        "Engine advies": "engine",
        // "Category": "category",
        // "Datum toegevoegd": "date_added",
        // "Laatste order": "lastOrder",
        // "Prijs": "price",

        /*
          avg_7d: 8.788461538461538
          brand: "Masli"
          category: "Eid & Ramadan"
          currentStock: 214
          date_added: "2021-12-12T13:31:39.000Z"
          engine: Object
          id: "2342"
          lastOrder: 1648309965000
          max_7d: 222
          min_7d: null
          name: "Ballonnenmix - Eid Mubarak Marmer Goud (10st)"
          orders: Object
          orders_1m: 414
          orders_1y: 457
          orders_7d: 232
          orders_prev_1m: 10
          orders_prev_1y: 0
          orders_prev_7d: 101
          pct_1m: 4040
          pct_1y: null
          pct_7d: 129.70297029702968
          price: 3.97
          reference: "2342"
        */
      },
      selectCustomization: (vm, filters) => vm.filters = filters,

      defaultColumns: [
        "ID", "Naam", "Voorraad", "7d", "1M", "1Y", "Omzet 1Y", "Engine advies"
      ],
      defaultColumnSizes: {
        id: 80,
        currentStock: 80,
        orders_7d: 80,
        orders_1m: 80,
        orders_1y: 80,
        engine: 80,
        pct_1y: 80,
      },
      columnConfigurationId: "advice-order",

      params: {
        search: "",
      },
      page_size: 30,
      toISOFields: ["initialDate", "finalDate"],

      parseParamsBeforeSending: () => ({}),

      route: "/drop/products",
    }),
  ],
  data() {
    return {
      productSettings: {},

      productOrderOverride: {},
      productSettingsSaved: false,
      productOrderOverrideSaved: false,
      filters: [],

      settings: {},
      form: {
        productSettings: {},
      },
      customLoading: false,
      selectedProduct: null,
    }
  },
  created() {
    this.loadSettings()
    const filters = localStorage.getItem("advice-order-filters")
    if (filters) this.filters = JSON.parse(filters)

    const brands = Array.from(this.availableBrands)
    const suppliers = Array.from(this.availableSuppliers)

    const brandsFilters = this.filters.filter(filter => filter.title === BRANDS_TITLE).map(filter => filter.value)
    const suppliersFilters = this.filters.filter(filter => filter.title === SUPPLIERS_TITLE).map(filter => filter.value)

    brandsFilters.forEach(filter => {
      if (!brands.includes(filter.value)) {
        this.filters.splice(this.filters.findIndex(item => filter.value === item.value), 1)
      }
    })
    suppliersFilters.forEach(filter => {
      if (!suppliers.includes(filter.value)) {
        this.filters.splice(this.filters.findIndex(item => filter.value === item.value), 1)
      }
    })
  },
  methods: {
    getSelectedExtraSections() {
      return this.filters
    },

    async updateProductOrder() {
      this.$handlePromise(async () => {
        await this.axios.post('/drop/products/order', this.productOrderOverride[this.selectedProduct.id])
        this.productOrderOverrideSaved = this.selectedProduct.id

        this.loadSettings(true, false)
        this.updateFetchCache()
      })
    },
    async updateProductSettings() {
      this.$handlePromise(async () => {
        this.productSettings[this.selectedProduct.id] = this.form.productSettings
        await this.axios.post('/drop/products/settings', this.productSettings[this.selectedProduct.id])
        this.productSettingsSaved = this.selectedProduct.id

        this.loadSettings(true, false)
        this.updateFetchCache()
      })
    },

    selectRow(item) {
      this.selectedProduct = item

      this.form.productSettings = Object.assign({}, this.productSettings[this.selectedProduct.id])
    },
    async loadSettings(fresh, showLoading = true) {
      if (showLoading) this.customLoading = true

      const [{data: productSettings}, {data: graphSettings}, {data: ordersData}] = await Promise.all([
        this.$store.dispatch("cachedGet", {
          endpoint: "/drop/products/settings",
          fresh,
        }),
        this.$store.dispatch("cachedGet", {
          endpoint: "/drop/settings",
          fresh,
        }),
        this.$store.dispatch("cachedGet", {
          endpoint: "/drop/products/order",
          fresh,
        }),
      ])

      if (!showLoading) return;

      this.settings = graphSettings.reduce((obj, curr) => ({
        ...obj,
        [curr.topic]: curr
      }), {})
      this.productSettings = productSettings.reduce((obj, curr) => ({
        ...obj,
        [curr.product_id]: curr,
      }), {});
      this.productOrderOverride = ordersData.reduce((obj, curr) => ({
        ...obj,
        [curr.product_id]: curr,
      }), {})

      this.customLoading = false
    },
    refreshAdviceOrder() {
      this.loadSettings(true)
      this.get(undefined, true)
    },
    showCustomerInformation(item) {
      this.$pushModal({
        modal: "CustomerInformation",
        toBind: {
          item,
        },
      })
    },

    settingsToPlotBands() {
      let plotBands = []
      let settingsToTake = ['y2019_ramadan', 'y2019_eid_fitr', 'y2019_eid_adha', 'y2019_december', 'y2019_other',
        'y2020_ramadan', 'y2020_eid_fitr', 'y2020_eid_adha', 'y2020_december', 'y2020_other',
        'y2021_ramadan', 'y2021_eid_fitr', 'y2021_eid_adha', 'y2021_december', 'y2021_other',
        'y2022_ramadan', 'y2022_eid_fitr', 'y2022_eid_adha', 'y2022_december', 'y2022_other']

      const settings = this.settings
      for (const settingToTake of settingsToTake) {
        if (!settings[settingToTake].value) {
          continue;
        }
        let year = settingToTake.split('y')[1].split('_')[0];
        let weekStart = (-.5 + parseInt(settings[settingToTake].value.trim())) + ''
        let weekEnd =   (.5 + parseInt(settings[settingToTake].value.trim())) + ''

        if (settings[settingToTake].value.indexOf(',') !== -1) {
          weekStart = settings[settingToTake].value.split(',')[0].trim()
          weekEnd = settings[settingToTake].value.split(',').slice(-1)[0].trim()
        }

        plotBands.push({
          color: '#FED8B1', // Color value
          from: mom(year + ' ' + weekStart, 'YYYY w').toDate().getTime(), // Start of the plot band
          to: mom(year + ' ' + weekEnd, 'YYYY w').toDate().getTime(), // End of the plot band
        });
      }

      return plotBands
    },
  },
  computed: {
    availableBrands() {
      return new Set(this.computedResult.map(prod => prod.brand).filter(brand => brand))
    },
    availableSuppliers() {
      return new Set(this.computedResult.map(prod => prod.supplier).filter(supplier => supplier))
    },
    expandCustomizations() {
      return [
        {
          title: "Filters",
          values: Object.keys(filters),
        },
        {
          title: BRANDS_TITLE,
          values: this.availableBrands,
        },
        {
          title: SUPPLIERS_TITLE,
          values: this.availableSuppliers,
        },
      ]
    },
    exportEndpointParams() {
      return {
        products: this.filteredResult.map(item => item.sku)
      }
    },
    computedResult() {
      const settings = this.productSettings

      const parseNumber = number => {
        if (!number) return "N/A"

        const parsed = parseFloat((number + "").replace(",", ".")).toFixed(0)
        return isNaN(parsed) ? "N/A" : `${parsed}%`
      }

      return this.result.map ? this.result.map(item => ({
        ...item,
        supplier: settings[item.id] ? settings[item.id].supplier : null,

        pct_1m_parsed: parseNumber(item.pct_1m),
        pct_1y_parsed: parseNumber(item.pct_1y),
        pct_7d_parsed: parseNumber(item.pct_7d),
        pct_1y: (item.orders_1y * item.price).toFixed(2),
      })) : []
    },
    filteredItems() {
      const search = this.search.toLowerCase()
      let filtered = this.computedResult.filter(item => {
        return item.name.toLowerCase().includes(search) ||
          item.id.toLowerCase().includes(search)
      })

      if (this.filters.length) {
        // Do not delete these references! They are used for giving a hint to VueJs reactivity system. If you remove them, then this computed property won't be reactive anymore.
        this.productOrderOverride
        // --------

        const mainFilters = this.filters.filter(filter => filter.title === "Filters") || []

        filtered = mainFilters.reduce((filtered, filter) => {
          return filtered.filter(prod => {
            return filters[filter.value](prod, this)
          })
        }, filtered)

        const brands = this.filters.filter(filter => filter.title === BRANDS_TITLE).map(filter => filter.value)
        const suppliers = this.filters.filter(filter => filter.title === SUPPLIERS_TITLE).map(filter => filter.value)

        if (brands.length) filtered = filtered.filter(prod => brands.includes(prod.brand))
        if (suppliers.length) filtered = filtered.filter(prod => suppliers.includes(prod.supplier))

        return filtered
      }
      return filtered
    },
    /// This is used on table.js
    filteredResult() {
      const filtered = this.filteredItems.slice()
      const property = this.sort
      const order = this.order

      property && filtered.sort((a, b) => {
        switch (property) {
          case "name": {
            return order === "asc"
              ? a[property].localeCompare(b[property])
              : b[property].localeCompare(a[property])
          }
          case "engine": {
            return this.sortNumber(a.engine.value, b.engine.value, true, order)
          }

          default: {
            return this.sortNumber(a[property], b[property], false, order)
          }
        }
      })

      return filtered
    },
    // --------
    loadedSettingsAndProducts() {
      return !this.loadingSettings && !this.isLoadingTableResult
    },
    tagColors() {
      return {
        brand: {backgroundColor: "#6c757d"},
        category: {backgroundColor: "#17a2b8"},
        added: {backgroundColor: "#ffc107", color: "currentColor !important"},
        supplier: {backgroundColor: "#007bff"},
      }
    },

    chartOptions() {
      const product = this.selectedProduct
      if (product) {
        return {
          title: {
            text: product.name
          },
          chart: {
            type: 'spline',
            zoomType: 'x',
            backgroundColor: "rgba(0,0,0,0)",
            height: 250,
          },
          yAxis: {
            title: {
              text: 'Aantallen'
            }
          },
          xAxis: {
            type: 'datetime',
            labels: {
              enabled: true,
              rotation: 45,
              align: 'left'
            },
            plotBands: this.settingsToPlotBands(),
          },
          plotOptions: {
            spline: {
              lineWidth: 4,
              states: {
                hover: {
                  lineWidth: 5
                }
              },
              pointInterval: 1000 * 60 * 60 * 24 * 7, // one week
              pointStart: new Date(product.date_added).getTime()
            }
          },
          series: [
            {
              name: 'verkocht',
              // for each order (per week),
              data: Object.entries(product.orders).map((h) => {
                return [ h[0], h[1]]
              }),
              color: '#555'
            }]
        };
      }
    },

    dangerRows() {
      const productSettings = this.productSettings
      return this.filteredResult.filter(({ id }) => productSettings[id] && productSettings[id].stopped).map(item => item.id)
    },
  },
  watch: {
    filters(filters) {
      localStorage.setItem("advice-order-filters", JSON.stringify(filters))
    },
    dangerRows(dangeredRows) {
      requestAnimationFrame(() => {
        this.filteredResult.forEach(({ id, name }) => {
          if (dangeredRows.includes(id)) {
            const item = document.querySelector(`#table-row-${id}`)
            item && item.classList.add("table-danger")
          } else {
            const item = document.querySelector(`#table-row-${id}`)
            item && item.classList.contains("table-danger") && item.classList.remove("table-danger")
          }
        })
      })
    },
    selectedProduct({id}, oldProduct) {
      if (id) {
        document.querySelector(`#table-row-${id}`).classList.add("selected-row")
      }
      if (oldProduct && oldProduct.id) {
        const oldRow = document.querySelector(`#table-row-${oldProduct.id}`)
        if (oldRow) oldRow.classList.remove("selected-row")
      }
    },
    loadedSettingsAndProducts(val) {
      if (val) {
        this.result.forEach(({id}) => {
          if (!this.settings[id]) {
            this.settings[id] = {product_id: id}
            this.productOrderOverride[id] = {product_id: id}
          }
        })
      }
    },
  },
}

</script>

<style>

.selected-row {
  border-top: 1px solid var(--mint-green) !important;
}

.selected-row + tr {
  border-top: 1px solid var(--mint-green) !important;
}

.selected-row td {
  background-color: rgba(50, 202, 210, .1);
}


.table-danger {
  border-top: 1px solid #ed969e !important;
}

.table-danger + tr {
  border-top: 1px solid #ed969e !important;
}

.table-danger td {
  background-color: #f5c6cb !important;
}

.table-danger:hover td {
  background-color: #ed9ba4 !important;
}

</style>

<style scoped>

#klantoverzicht::v-deep .content-wrapper > .content-wrapper {
  flex-direction: column-reverse;
}


.graph-wrapper {
  width: 100% !important;
  min-width: unset;
  max-width: unset;
}

.empty-placeholder-wrapper {
  margin: 70px 0;
}

@media (min-width: 920px) {
  #klantoverzicht::v-deep .content-wrapper > .content-wrapper {
    flex-direction: row;
  }

  .graph-wrapper {
    min-width: 500px !important;
    width: unset;
    max-width: 500px;
  }
}

@media (min-width: 1300px) {

  .graph-wrapper {
    min-width: 700px !important;
    width: unset;
    max-width: 700px;
  }
}

h4 {
  font-weight: 500 !important;
  margin: 0px;
  margin-bottom: 4px;
}

.graph-details {
  font-weight: 300 !important;
  font-size: 18px;
}

b {
  font-weight: 600 !important;
}

.section {
  margin: 14px 0;
  border-top: 1px solid rgba(0,0,0,.1);
}

p {
  margin: 0px;
}

.tag {
  font-size: 10px !important;
  border-radius: 4px;
  padding: 2px 5px;
  color: white;
  font-weight: 600;
}

.tag + .tag {
  margin-left: 4px;
}

.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 1000px;
}

.name-wrapper {
  color: currentColor;
}

.name-wrapper:hover {
  text-decoration: underline;
}

.date-picker-btn {
  background-color: green !important;
}

.grid-filters {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr 1fr;
}

.chart {
  border-left: 1px solid #EFEFF0;
}

.chart, .chart > div {
  height: 100%;
}

@media (max-width: 1000px) {
  .grid-filters {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .chart {
    margin: 0 !important;
    margin-top: 3px !important;
  }
}

@media (max-width: 800px) {
  .grid-filters {
    grid-template-columns: 1fr;
  }
}

</style>

<style>

.advice-order-filters .date-picker-button {
  padding: 9.5px !important;
}
</style>
